import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '../ui/Button.tsx';
import { Input } from '../ui/Input.tsx';
import { Modal } from './Modal.tsx';

/**
 * Confirmation dialog for destructive / high-stakes actions. Mirrors
 * the console-web `ConfirmDialog` prop contract so page migrations stay
 * mechanical, but it is built on this app's own `Modal` (focus trap,
 * Escape, backdrop close) — nxtpath deliberately carries no Radix
 * dialog dependency.
 *
 * With `typeToConfirm` set, the confirm button stays disabled until the
 * operator types the exact string.
 *
 * Copy is caller-supplied: this layer ships no strings of its own.
 */
interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly body?: ReactNode;
  readonly confirmLabel: string;
  /** Pass translated strings — the library ships no copy of its own. */
  readonly cancelLabel: string;
  readonly closeLabel: string;
  readonly onConfirm: () => void;
  readonly tone?: 'default' | 'danger';
  readonly typeToConfirm?: string;
  readonly typeToConfirmLabel?: ReactNode;
  readonly loading?: boolean;
  readonly testId?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  closeLabel,
  onConfirm,
  tone = 'default',
  typeToConfirm,
  typeToConfirmLabel,
  loading = false,
  testId,
}: ConfirmDialogProps): JSX.Element | null {
  const [typed, setTyped] = useState('');
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const bodyId = `${generatedId}-body`;
  const typeInputId = `${generatedId}-type`;

  useEffect(() => {
    if (!open) {
      setTyped('');
    }
  }, [open]);

  const hasBody = body !== undefined && body !== null;
  const needsTyping = typeof typeToConfirm === 'string' && typeToConfirm.length > 0;
  const confirmDisabled = loading || (needsTyping && typed !== typeToConfirm);

  function requestClose(): void {
    if (!loading) {
      onOpenChange(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={requestClose}
      titleId={titleId}
      descriptionId={hasBody ? bodyId : undefined}
      testId={testId}
      closeOnBackdrop={!loading}
    >
      <div className="flex flex-col gap-5 p-6">
        <div className="flex items-start gap-3">
          <h2 id={titleId} className="m-0 min-w-0 flex-1 text-title text-ink">
            {title}
          </h2>
          <button
            type="button"
            aria-label={closeLabel}
            onClick={requestClose}
            disabled={loading}
            data-testid={testId !== undefined ? `${testId}-close` : undefined}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-low transition-colors duration-150 hover:bg-active hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:pointer-events-none disabled:opacity-50"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        {hasBody ? (
          <div id={bodyId} className="text-body text-ink-mid">
            {body}
          </div>
        ) : null}

        {needsTyping ? (
          <div className="flex flex-col gap-1.5">
            {typeToConfirmLabel !== undefined && typeToConfirmLabel !== null ? (
              <label htmlFor={typeInputId} className="m-0 text-label text-ink-mid">
                {typeToConfirmLabel}
              </label>
            ) : null}
            <p className="m-0 select-all rounded bg-well px-2.5 py-1.5 font-mono text-mono text-ink">
              {typeToConfirm}
            </p>
            <Input
              id={typeInputId}
              mono
              value={typed}
              onChange={(event) => setTyped(event.currentTarget.value)}
              disabled={loading}
              data-testid={testId !== undefined ? `${testId}-type-input` : undefined}
            />
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={requestClose}
            disabled={loading}
            data-testid={testId !== undefined ? `${testId}-cancel` : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
            loading={loading}
            data-testid={testId !== undefined ? `${testId}-confirm` : undefined}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
